import type {
    AnalyticsProperties,
    AnalyticsPropertyValue,
} from './types';

/** Canonical form: lowercase alphanumeric only (`phone_number` / `phone-number` → `phonenumber`). */
function normalizePropertyName(key: string): string {
    return key.toLowerCase().replace(/[^a-z0-9]/g, '');
}

const FORBIDDEN_PROPERTY_KEYS = new Set(
    [
        'email',
        'phone',
        'phonenumber',
        'firstname',
        'lastname',
        'fullname',
        'ssn',
        'docnumber',
        'documentnumber',
        'passport',
        'accountid',
        'otp',
        'code',
        'token',
        'accesstoken',
        'authorization',
        'password',
        'redirecturl',
        'partnerredirecturl',
        'birthdate',
        'dob',
        'dateofbirth',
        'address',
        'ip',
        'ipaddress',
        'location',
        'deviceid',
        'fingerprint',
        'user',
        'username',
        'userid',
        'customerid',
        'clientid',
        'jwt',
        'cookie',
        'session',
        'sessionid',
    ].map(normalizePropertyName)
);

const RESERVED_ENRICHMENT_KEYS = new Set(
    [
        'environment',
        'language',
        'application_id',
        'customer_type',
        'screen_name',
    ].map(normalizePropertyName)
);

function isAllowedPropertyValue(
    value: unknown
): value is AnalyticsPropertyValue {
    const t = typeof value;
    return (
        value === null ||
        t === 'string' ||
        t === 'number' ||
        t === 'boolean'
    );
}

/**
 * Sanitize + validate in one pass.
 * Returns cleaned properties, or null if the event must be dropped
 * (forbidden / non-primitive keys). Never sends partial events.
 */
export function validateProperties(
    event: string,
    properties: AnalyticsProperties,
    debug: boolean
): AnalyticsProperties | null {
    const cleaned: AnalyticsProperties = {};
    const forbiddenKeys: string[] = [];
    const invalidValueKeys: string[] = [];

    for (const [key, value] of Object.entries(properties)) {
        if (value === undefined) {
            continue;
        }

        const normalized = normalizePropertyName(key);
        if (RESERVED_ENRICHMENT_KEYS.has(normalized)) {
            continue;
        }
        if (FORBIDDEN_PROPERTY_KEYS.has(normalized)) {
            forbiddenKeys.push(key);
            continue;
        }
        if (!isAllowedPropertyValue(value)) {
            invalidValueKeys.push(key);
            continue;
        }

        cleaned[key] = value;
    }

    if (forbiddenKeys.length === 0 && invalidValueKeys.length === 0) {
        return cleaned;
    }

    if (debug) {
        const reasons: string[] = [];
        if (forbiddenKeys.length > 0) {
            reasons.push(`Forbidden properties: ${forbiddenKeys.join(', ')}`);
        }
        if (invalidValueKeys.length > 0) {
            reasons.push(
                `Non-primitive properties: ${invalidValueKeys.join(', ')}`
            );
        }
        // eslint-disable-next-line no-console
        console.warn(
            `[Analytics] Event rejected: "${event}". ${reasons.join('. ')}`
        );
    }

    return null;
}
